import { compareExtendedSubscriptionLevel } from '@ps/features/subscription/configuration/functions/subscription-display';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models';

import type { BioAccessGuard } from '../components/access-guard/guard-descriptor';

/**
 * Resolve the viewer-dependent half of a `subscription` gate: when the viewer
 * already holds at least the required tier, the gate is satisfied, so downgrade
 * it to `skip-follow` (proceed) rather than routing to the subscribe picker.
 * Any non-`subscription` guard, or an unknown/insufficient tier, passes through
 * unchanged. Keeps `AccessGuard` viewer-independent — the held-tier check is
 * resolved by the caller from live provider state (`extendedSubscriptionLevel`
 * from `useBioUserAccess`).
 *
 * Extracted so the tier comparison lives in ONE place instead of being
 * copy-pasted in every subscription-gated wrapper (PDP `BuyNowAction`, the bio
 * custom-button `WiredCustomButtonLink`).
 */
export function downgradeIfTierHeld(
  guard: BioAccessGuard,
  extendedLevel: PopStoreExtendedSubscriptionLevel | undefined,
): BioAccessGuard {
  if (guard.kind !== 'subscription') return guard;
  const holdsRequiredTier =
    extendedLevel != null &&
    compareExtendedSubscriptionLevel(
      extendedLevel,
      guard.level as unknown as PopStoreExtendedSubscriptionLevel,
    ) >= 0;
  return holdsRequiredTier ? { ...guard, kind: 'skip-follow' } : guard;
}
